import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  MapPin,
  Calendar,
  Send,
  Eye,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Waves,
  Fish,
  Search,
  Compass,
  User,
  Clock,
  Heart,
  Loader2,
  Info,
  ChevronRight
} from 'lucide-react';
import { fetchCollection, submitCmsEntry } from '../lib/cms';
import { getOptimizedImageUrl } from '../lib/utils';

const OTTO_HERO_IMAGE = 'https://assets.auxodev.dk/assets/b3f2b1ea-cc94-4b30-89c2-3e141ee932d1/bc49f88c-88e4-4e70-a05a-7ce25ab1fcb9.png';
const OTTO_SUNBATHING = 'https://assets.auxodev.dk/assets/b3f2b1ea-cc94-4b30-89c2-3e141ee932d1/b2672745-c405-419d-a3cc-ec7f5c0a2655.png';

const ACTIVITY_OPTIONS = [
  'Spiste en fisk',
  'Svømmede nysgerrigt forbi',
  'Solede sig på sten/badebro',
  'Legede i overfladen',
  'Jagede i sivene',
  'Dukkede op ved kano/kajak',
  'Andet'
];

export default function Sightings() {
  const [sightings, setSightings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocationFilter, setSelectedLocationFilter] = useState('Alle');

  // Form State for CMS Submission
  const getTodayString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const initialFormState = {
    spotter_name: '',
    location: '',
    sighting_date: getTodayString(),
    activity: 'Svømmede nysgerrigt forbi',
    description: '',
    website_url: '', // Honeypot field (must stay empty)
  };

  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('idle'); // idle | success | error
  const [statusMessage, setStatusMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    loadSightings();
  }, []);

  const loadSightings = async () => {
    setLoading(true);
    try {
      const data = await fetchCollection('otto_sightings');
      // Sort by sighting_date descending
      const sorted = [...data].sort((a, b) => {
        const dateA = new Date(a.sighting_date || a.updatedAt || 0).getTime();
        const dateB = new Date(b.sighting_date || b.updatedAt || 0).getTime();
        return dateB - dateA;
      });
      setSightings(sorted);
    } catch (err) {
      console.error('Fejl ved hentning af observationer:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus('idle');
    setStatusMessage('');
    setFieldErrors({});

    // Client-side quick checks
    const errors = {};
    if (!formData.spotter_name.trim()) {
      errors.spotter_name = 'Angiv venligst hvem der har set Otto';
    }
    if (!formData.location.trim()) {
      errors.location = 'Angiv venligst hvor Otto blev set';
    }
    if (!formData.sighting_date) {
      errors.sighting_date = 'Vælg dato for observationen';
    }
    if (!formData.description.trim()) {
      errors.description = 'Skriv en kort beskrivelse af oplevelsen';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setSubmitting(false);
      return;
    }

    try {
      // POST to CMS visitor submissions endpoint
      // Honeypot website_url must be included
      const payload = {
        spotter_name: formData.spotter_name.trim(),
        location: formData.location.trim(),
        sighting_date: formData.sighting_date,
        description: formData.description.trim(),
        activity: formData.activity || 'Observation',
        website_url: formData.website_url, // Honeypot
      };

      const result = await submitCmsEntry('otto_sightings', payload);

      setSubmitStatus('success');
      setStatusMessage(
        result.message ||
          'Tusind tak for din observation! Den er modtaget og sendt til godkendelse hos redaktionen, før den bliver vist her på siden.'
      );
      setFormData(initialFormState);
    } catch (err) {
      console.error('Fejl ved indsendelse af sighting:', err);
      setSubmitStatus('error');
      setStatusMessage(
        err.message ||
          'Der opstod en fejl under indsendelsen. Tjek venligst dine oplysninger og prøv igen.'
      );
      if (err.details && err.details.errors) {
        setFieldErrors(err.details.errors);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('da-DK', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  // Extract unique locations for quick filters
  const locationFilters = ['Alle', 'Aalborg', 'Nibe', 'Fur', 'Hvalpsund', 'Skive'];

  // Filtered sightings
  const filteredSightings = sightings.filter((item) => {
    const matchesSearch =
      (item.spotter_name && item.spotter_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item.location && item.location.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item.activity && item.activity.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesLocation =
      selectedLocationFilter === 'Alle' ||
      (item.location && item.location.toLowerCase().includes(selectedLocationFilter.toLowerCase()));

    return matchesSearch && matchesLocation;
  });

  return (
    <>
      <Helmet>
        <title>Spot Otto | Har du set LimfjordsOdderen? | LimfjordsOdderen</title>
        <meta
          name="description"
          content="Har du set Otto i Limfjorden? Indberet din observation, se hvor andre har spottet ham og følg med i hans færden rundt i fjorden."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-blue-50 via-cyan-50/40 to-white pt-12 pb-16 lg:pt-20 lg:pb-24 border-b border-blue-100">
        <div className="absolute inset-0 pointer-events-none opacity-40">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-blue-200/40 blur-3xl" />
          <div className="absolute bottom-0 left-10 w-80 h-80 rounded-full bg-cyan-200/40 blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Hero Text */}
            <div className="lg:col-span-7 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-100/80 border border-blue-200 text-blue-800 text-xs sm:text-sm font-semibold mb-6 shadow-xs">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-600" />
                </span>
                Limfjordens Otto-Radar • Live Observationer
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-tight">
                Hvor har du{' '}
                <span className="bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-700 bg-clip-text text-transparent">
                  set Otto?
                </span>
              </h1>

              <p className="mt-5 text-lg sm:text-xl text-slate-600 max-w-2xl leading-relaxed">
                Folk fra hele Limfjorden melder ind, når Otto stikker snuden op af det salte fjordvand.
                Har du også spottet ham spise en sild på broen eller svømme forbi ved kysten?
                Hjælp os med at kortlægge hans eventyr!
              </p>

              {/* Action Buttons */}
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
                <a
                  href="#meld-ind-form"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-base shadow-md hover:shadow-lg transition-all duration-200 group"
                >
                  <Eye className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  Meld din observation ind
                </a>
                <a
                  href="#observationer-liste"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-white hover:bg-slate-50 text-slate-700 font-semibold text-base border border-slate-200 shadow-xs transition-colors"
                >
                  <Waves className="w-5 h-5 text-cyan-600" />
                  Se seneste observationer ({sightings.length})
                </a>
              </div>

              {/* Live Status Row */}
              <div className="mt-10 pt-8 border-t border-blue-100/80 grid grid-cols-3 gap-4 text-center sm:text-left">
                <div>
                  <div className="text-2xl sm:text-3xl font-extrabold text-blue-600">{sightings.length}+</div>
                  <div className="text-xs sm:text-sm text-slate-500 font-medium">Godkendte spots</div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-extrabold text-cyan-600">6+</div>
                  <div className="text-xs sm:text-sm text-slate-500 font-medium">Fjorde & havne</div>
                </div>
                <div>
                  <div className="text-2xl sm:text-3xl font-extrabold text-emerald-600">100%</div>
                  <div className="text-xs sm:text-sm text-slate-500 font-medium">Vild & fri odder</div>
                </div>
              </div>
            </div>

            {/* Hero Visual */}
            <div className="lg:col-span-5 relative">
              <div className="relative mx-auto max-w-md lg:max-w-none">
                <div className="aspect-4/3 rounded-3xl overflow-hidden shadow-2xl border-4 border-white bg-slate-100">
                  <img
                    src={getOptimizedImageUrl(OTTO_HERO_IMAGE, { width: 900, quality: 85 })}
                    alt="Otto oddere kigger nysgerrigt op af Limfjorden"
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Floating Sighting Badge */}
                <div className="absolute -bottom-6 -left-4 sm:left-6 bg-white/95 backdrop-blur-md rounded-2xl p-4 shadow-xl border border-blue-100 max-w-xs flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center shrink-0 text-blue-600">
                    <Fish className="w-6 h-6" />
                  </div>
                  <div className="text-left">
                    <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider">Seneste melding</div>
                    <div className="text-sm font-bold text-slate-900 truncate">
                      {sightings[0]?.location || 'Aalborg Havnefront'}
                    </div>
                    <div className="text-xs text-slate-500">
                      Set af {sightings[0]?.spotter_name || 'Lokale borgere'}
                    </div>
                  </div>
                </div>

                <div className="absolute -top-4 -right-4 bg-amber-400 text-amber-950 font-bold text-xs px-3 py-1.5 rounded-full shadow-md flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" />
                  Ny CMS Submission
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Main Content Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {/* LEFT: Submission Form */}
          <div className="lg:col-span-5">
            <div id="meld-ind-form" className="sticky top-24 bg-white rounded-3xl border border-blue-100 shadow-xl shadow-blue-500/5 p-6 sm:p-8">
              
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center">
                  <Send className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Meld en observation</h2>
                  <p className="text-xs text-slate-500">Hjælp os med at holde øje med Otto</p>
                </div>
              </div>

              <div className="mb-6 p-4 rounded-2xl bg-blue-50/80 border border-blue-100 text-xs text-blue-900 flex items-start gap-2.5">
                <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                <span>
                  <strong>Bemærk:</strong> For at passe godt på Otto og undgå spam, bliver alle indberetninger modereret og godkendt i CMS'et, før de vises offentligt.
                </span>
              </div>

              {/* Status Banner */}
              {submitStatus === 'success' && (
                <div className="mb-6 p-5 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-900 animate-in fade-in duration-300">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-bold text-sm text-emerald-950">Observation modtaget! 🎉</h4>
                      <p className="mt-1 text-xs text-emerald-800 leading-relaxed">{statusMessage}</p>
                      <button
                        type="button"
                        onClick={() => setSubmitStatus('idle')}
                        className="mt-3 text-xs font-semibold text-emerald-700 hover:text-emerald-900 underline cursor-pointer"
                      >
                        Indberet endnu en observation
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-900 animate-in fade-in duration-300">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-bold text-sm text-rose-950">Fejl ved indsendelse</h4>
                      <p className="mt-1 text-xs text-rose-800">{statusMessage}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* The Submission Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                
                {/* MANDATORY Honeypot field - completely hidden for spam bots */}
                <div className="sr-only absolute -top-9999px -left-9999px" aria-hidden="true">
                  <label htmlFor="website_url">Lad dette felt være tomt hvis du er et menneske</label>
                  <input
                    id="website_url"
                    name="website_url"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website_url}
                    onChange={handleInputChange}
                  />
                </div>

                {/* Spotter Name (Hvem har set Otto) */}
                <div>
                  <label htmlFor="spotter_name" className="block text-sm font-semibold text-slate-800 mb-1.5">
                    Hvem så Otto? <span className="text-blue-600">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <User className="w-4 h-4" />
                    </div>
                    <input
                      id="spotter_name"
                      name="spotter_name"
                      type="text"
                      required
                      placeholder="F.eks. Camilla & Jonas / Familien Hansen"
                      value={formData.spotter_name}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-4 py-2.5 rounded-xl border bg-slate-50/50 text-slate-900 text-sm placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                        fieldErrors.spotter_name ? 'border-rose-400 focus:border-rose-500' : 'border-slate-200 focus:border-blue-500'
                      }`}
                    />
                  </div>
                  {fieldErrors.spotter_name && (
                    <p className="mt-1 text-xs text-rose-600">{fieldErrors.spotter_name}</p>
                  )}
                </div>

                {/* Location (Hvor blev Otto set) */}
                <div>
                  <label htmlFor="location" className="block text-sm font-semibold text-slate-800 mb-1.5">
                    Hvor blev han set? <span className="text-blue-600">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <MapPin className="w-4 h-4" />
                    </div>
                    <input
                      id="location"
                      name="location"
                      type="text"
                      required
                      placeholder="F.eks. Nibe Havn, Fur Klint eller Limfjordsbroen"
                      value={formData.location}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-4 py-2.5 rounded-xl border bg-slate-50/50 text-slate-900 text-sm placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                        fieldErrors.location ? 'border-rose-400 focus:border-rose-500' : 'border-slate-200 focus:border-blue-500'
                      }`}
                    />
                  </div>
                  {fieldErrors.location && (
                    <p className="mt-1 text-xs text-rose-600">{fieldErrors.location}</p>
                  )}
                </div>

                {/* Date of Sighting */}
                <div>
                  <label htmlFor="sighting_date" className="block text-sm font-semibold text-slate-800 mb-1.5">
                    Dato for observationen <span className="text-blue-600">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <Calendar className="w-4 h-4" />
                    </div>
                    <input
                      id="sighting_date"
                      name="sighting_date"
                      type="date"
                      required
                      value={formData.sighting_date}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-4 py-2.5 rounded-xl border bg-slate-50/50 text-slate-900 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all ${
                        fieldErrors.sighting_date ? 'border-rose-400 focus:border-rose-500' : 'border-slate-200 focus:border-blue-500'
                      }`}
                    />
                  </div>
                  {fieldErrors.sighting_date && (
                    <p className="mt-1 text-xs text-rose-600">{fieldErrors.sighting_date}</p>
                  )}
                </div>

                {/* Activity Dropdown */}
                <div>
                  <label htmlFor="activity" className="block text-sm font-semibold text-slate-800 mb-1.5">
                    Hvad lavede Otto?
                  </label>
                  <select
                    id="activity"
                    name="activity"
                    value={formData.activity}
                    onChange={handleInputChange}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-slate-50/50 text-slate-900 text-sm focus:bg-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                  >
                    {ACTIVITY_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-sm font-semibold text-slate-800 mb-1.5">
                    Beskriv oplevelsen <span className="text-blue-600">*</span>
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows={4}
                    required
                    placeholder="Fortæl hvad der skete! Hvordan så han ud? Hvor længe var han der? Gjorde han noget sjovt?"
                    value={formData.description}
                    onChange={handleInputChange}
                    className={`w-full p-3.5 rounded-xl border bg-slate-50/50 text-slate-900 text-sm placeholder:text-slate-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all resize-y ${
                      fieldErrors.description ? 'border-rose-400 focus:border-rose-500' : 'border-slate-200 focus:border-blue-500'
                    }`}
                  />
                  {fieldErrors.description && (
                    <p className="mt-1 text-xs text-rose-600">{fieldErrors.description}</p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full mt-2 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold text-sm shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Indsender til Otto-redaktionen...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Indsend observation til godkendelse
                    </>
                  )}
                </button>
              </form>

            </div>
          </div>

          {/* RIGHT: Sightings Feed */}
          <div className="lg:col-span-7" id="observationer-liste">
            
            {/* Header with Search & Filter */}
            <div className="bg-white rounded-3xl border border-slate-200/80 p-6 sm:p-8 mb-8 shadow-xs">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Seneste Otto-observationer</h2>
                  <p className="text-sm text-slate-500 mt-0.5">
                    Se hvad andre eventyrere har set langs fjorden
                  </p>
                </div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-50 border border-cyan-200 text-cyan-800 text-xs font-semibold self-start sm:self-auto">
                  <Waves className="w-3.5 h-3.5" />
                  Live CMS feed
                </div>
              </div>

              {/* Search Bar */}
              <div className="relative mb-5">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <Search className="w-4 h-4" />
                </div>
                <input
                  type="text"
                  placeholder="Søg på sted, spotter eller aktivitet..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50/60 text-slate-900 text-sm placeholder:text-slate-400 focus:bg-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                />
              </div>

              {/* Location Filter Chips */}
              <div className="flex items-center gap-2 overflow-x-auto pb-1 text-xs">
                <span className="text-slate-400 font-medium whitespace-nowrap mr-1">Filtrér område:</span>
                {locationFilters.map((loc) => (
                  <button
                    key={loc}
                    onClick={() => setSelectedLocationFilter(loc)}
                    className={`px-3 py-1.5 rounded-lg font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      selectedLocationFilter === loc
                        ? 'bg-blue-600 text-white shadow-xs'
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                    }`}
                  >
                    {loc}
                  </button>
                ))}
              </div>
            </div>

            {/* Sightings Feed Cards */}
            {loading ? (
              <div className="p-12 text-center bg-white rounded-3xl border border-slate-100">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
                <p className="text-sm font-medium text-slate-600">Henter observationer fra fjorden...</p>
              </div>
            ) : filteredSightings.length === 0 ? (
              <div className="p-12 text-center bg-white rounded-3xl border border-slate-200/80">
                <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-500 flex items-center justify-center mx-auto mb-4">
                  <Compass className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">Ingen observationer fundet</h3>
                <p className="mt-1 text-sm text-slate-500 max-w-sm mx-auto">
                  Der matcher desværre ingen observationer med dine søgekriterier. Prøv at søge på noget andet eller nulstil filteret.
                </p>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedLocationFilter('Alle');
                  }}
                  className="mt-4 px-4 py-2 rounded-xl bg-blue-50 text-blue-600 font-semibold text-xs hover:bg-blue-100 transition-colors"
                >
                  Nulstil søgning og filtre
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {filteredSightings.map((sighting, index) => (
                  <div
                    key={sighting.id || index}
                    className="bg-white rounded-3xl border border-slate-200/80 p-6 sm:p-7 shadow-xs hover:shadow-md hover:border-blue-200 transition-all duration-200 group"
                  >
                    {/* Top Row: Location & Date */}
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-800 text-xs font-semibold">
                        <MapPin className="w-3.5 h-3.5 text-blue-600" />
                        {sighting.location || 'Limfjorden'}
                      </div>
                      
                      <div className="inline-flex items-center gap-1.5 text-xs text-slate-400 font-medium">
                        <Calendar className="w-3.5 h-3.5 text-slate-400" />
                        {formatDate(sighting.sighting_date)}
                      </div>
                    </div>

                    {/* Sighting Description */}
                    <p className="text-slate-700 text-sm sm:text-base leading-relaxed mb-5">
                      "{sighting.description}"
                    </p>

                    {/* Meta Row: Spotter & Activity */}
                    <div className="pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-blue-500 to-cyan-400 text-white flex items-center justify-center font-bold text-xs shadow-xs">
                          {sighting.spotter_name ? sighting.spotter_name.charAt(0).toUpperCase() : 'O'}
                        </div>
                        <div>
                          <span className="text-slate-400">Indberettet af: </span>
                          <span className="font-semibold text-slate-900">{sighting.spotter_name || 'Anonym odder-fan'}</span>
                        </div>
                      </div>

                      {sighting.activity && (
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-50 text-amber-800 font-medium">
                          <Sparkles className="w-3 h-3 text-amber-600" />
                          {sighting.activity}
                        </div>
                      )}
                    </div>

                  </div>
                ))}
              </div>
            )}

            {/* Respectful Otter Watching Tips */}
            <div className="mt-12 bg-gradient-to-br from-slate-900 to-blue-950 rounded-3xl p-8 text-white shadow-xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-blue-500/20 border border-blue-400/30 flex items-center justify-center text-blue-300">
                  <Heart className="w-5 h-5 fill-current" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">God odder-etikette ved Limfjorden</h3>
                  <p className="text-xs text-slate-300">Pas på Otto og hans venner</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs sm:text-sm text-slate-300">
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0 mt-2" />
                  <span>
                    <strong>Hold god afstand:</strong> Oddere er vilde dyr. Brug en kikkert eller zoom på kameraet frem for at gå tæt på.
                  </span>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0 mt-2" />
                  <span>
                    <strong>Hold hunden i snor:</strong> Særligt ved rørskove og kyststrækninger, hvor oddere bygger deres huler (gyl).
                  </span>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0 mt-2" />
                  <span>
                    <strong>Daggry & skumring:</strong> Otto er mest aktiv tidligt om morgenen og i skumringen, når vandet ligger blikstille.
                  </span>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 shrink-0 mt-2" />
                  <span>
                    <strong>Ingen fodring:</strong> Otto fanger selv sine lækre sild og skrubber – giv ham ikke menneskemad.
                  </span>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>
    </>
  );
}
